import get from 'lodash-es/get'
import {
  Site,
  customerMarketShopfrontApi,
  customerMarketShopfrontJobsApi
} from '@envato/foundation-types'

import { getCurrentSiteDomain } from './sites'
import {
  reportProductClick,
  reportProductAddToCartAttempt,
  reportModalPageView,
  reportAudioPreviewDownload,
  reportVideoPreviewDownload
} from '../analytics/resultAnalytics'
import { logError } from './errors'
import { getTopLevelRoute } from './urlHelper'
import { getConfigBySite } from '../../config/sites'
import { ICommonLocation, IFlatQuery } from './location/types'
import { IItem, IDecoratedItem } from '../../typings/searchResults'
import { IHomePageApiData } from '../../common/actions/homePage'

export const formatItemNameForPreview = (item: IItem) => {
  const id = get(item, 'id', '')
  const name = get(item, 'name', '').replace(/\W/g, '-')
  const author = get(item, 'author_username', '').replace(/\W/g, '-')

  return `${id}_${name}_by_${author}_preview.mp3`.toLowerCase()
}

export const isSearchResultMatchesEqual = (
  mainMatches: customerMarketShopfrontApi.search.Match[],
  comparingMatches: customerMarketShopfrontApi.search.Match[]
) => {
  const hasSameLength = mainMatches.length === comparingMatches.length
  if (!hasSameLength) {
    return false
  }
  const comparisonResults = mainMatches
    .map((item, index) => {
      return item && comparingMatches && item.id === comparingMatches[index].id
    })
    .every(isIdEqual => isIdEqual)

  return comparisonResults
}

// https://github.com/envato/foundation-docs/blob/master/docs/shopfront_special_ui_rules.md
export const filterSortByOptions = (
  currentSite: Site,
  parsedLocation: IFlatQuery,
  options: any
) => {
  let filteredOptions = [...options]
  // no ratings for photodune
  if (currentSite === Site.photodune) {
    filteredOptions = filteredOptions.filter(options => {
      return options.value !== 'rating'
    })
  }

  // no `Best Match` if there's no search term
  if (!parsedLocation.term) {
    filteredOptions = filteredOptions.filter(options => {
      return options.value !== 'relevance'
    })
  }
  return filteredOptions
}

export const getItemAttribute = (item: IItem, name: string) => {
  return get(
    item.attributes.find(attribute => attribute.name === name),
    'value'
  )
}

export const getItemRankOffset = (currentSite: Site, currentPage: number) => {
  const pageSize = getConfigBySite(currentSite, 'common.search_page_size')
  return ((currentPage || 1) - 1) * pageSize + 1
}

export type ItemDecoratorFunction = (
  item: IItem,
  index: number
) => IDecoratedItem

export const itemDecorator = (
  currentSite: Site,
  openModal: Function,
  signedOut: boolean,
  favoriteCollectionID: number,
  collections: any,
  deleteBookmark: Function,
  addBookmark: Function,
  createFavoriteCollection: () => Promise<any>,
  location: ICommonLocation,
  listName?: string
): ItemDecoratorFunction => {
  return (item, index) => {
    const { isInFavourites } = item.ui

    const topLevelRoute = getTopLevelRoute(location.pathname)
    const defaultListName =
      topLevelRoute === 'category'
        ? 'Search Results: Category'
        : 'Search Results: Search'
    const reportProductClickHandler = reportProductClick.bind(null, {
      index,
      item,
      currentSite,
      listName: listName || defaultListName
    })
    const reportProductAddToCartAttemptHandler = reportProductAddToCartAttempt.bind(
      null,
      {
        item,
        index,
        currentSite,
        pageType: topLevelRoute
      }
    )

    const onAddToFavourites = (event: React.SyntheticEvent) => {
      event.preventDefault()
      if (signedOut) {
        ;(window as any).location = `//${getCurrentSiteDomain(
          currentSite
        )}/sign_in?action_name=favorite&resource_id=${item.id}&return_to=${
          window.location.pathname
        }${window.location.search}`
      } else {
        // check whether user has favorite collection
        if (favoriteCollectionID) {
          if (isInFavourites) {
            const bookmark =
              collections[favoriteCollectionID].bookmarks[item.id]
            if (bookmark.id && bookmark.optimisticStatus !== 'pending') {
              const isFavouriteCollection = true
              deleteBookmark(
                favoriteCollectionID,
                item.id,
                bookmark.id,
                isFavouriteCollection
              )
            }
          } else {
            const isFavouriteCollection = true
            addBookmark(favoriteCollectionID, item.id, isFavouriteCollection)
          }
        } else {
          createFavoriteCollection()
            .then(action => {
              if (action && action.type === 'createCollectionSuccess') {
                const newCollectionId = action.payload.id
                // this is used for ga tracking so we know it's add to favourite collection
                const isFavouriteCollection = true
                return addBookmark(
                  newCollectionId,
                  item.id,
                  isFavouriteCollection
                )
              }
            })
            .catch(logError)
        }
      }
    }

    const onAddToCollections = (event: React.SyntheticEvent) => {
      event.preventDefault()
      if (signedOut) {
        ;(window as any).location = `//${getCurrentSiteDomain(
          currentSite
        )}/sign_in?action_name=collection&resource_id=${item.id}&return_to=${
          window.location.pathname
        }${window.location.search}`
      } else {
        reportModalPageView({
          title: 'Modal: Add item to collection',
          page: `${window.location.pathname}/add-item-to-collection${
            window.location.search
          }`
        })
        openModal({
          type: 'addItemToCollection',
          payload: {
            item
          }
        })
      }
    }

    const onAddToCart = (event: React.SyntheticEvent) => {
      event.preventDefault()
      reportProductAddToCartAttemptHandler()

      reportModalPageView({
        title: 'Modal: Customize item before adding to cart',
        page: `${
          window.location.pathname
        }/customize-item-before-adding-to-cart${window.location.search}`
      })
      openModal({
        type: 'addItemToCart',
        payload: {
          item
        }
      })
    }

    const onDownloadAudioPreview = async () => {
      reportAudioPreviewDownload(item.id)
    }

    const onDownloadVideoPreview = async () => {
      reportVideoPreviewDownload(item.id)
    }

    return {
      item,
      onItemLinkClick: reportProductClickHandler,
      onAddToCart,
      onAddToFavourites,
      onAddToCollections,
      onDownloadAudioPreview,
      onDownloadVideoPreview
    }
  }
}

export const getAllItemsIds = (data: {}) =>
  Object.values(data)
    .map((e: { matches?: customerMarketShopfrontJobsApi.IItem[] }) => e.matches)
    .filter(e => !!e)
    .flat()
    .map((item: IItem) => item.id)

export const limitItemsForNewDesign = (data: IHomePageApiData) => ({
  ...data,
  featured_items: {
    matches: get(data, 'featured_items.matches', []).slice(0, 4)
  },
  newest_items: {
    ...data.newest_items,
    matches: get(data, 'newest_items.matches', []).slice(0, 8)
  }
})
